import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LoginForm from './components/LoginForm';
import Dashboard from './components/Dashboard';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import FacilitiesPage from './pages/FacilitiesPage';
import BookingsImportPage from './pages/BookingsImportPage';
import BookingsPage from './pages/BookingsPage';
import OrdersPage from './pages/OrdersPage';
import UsersPage from './pages/UsersPage';
import InvoicesPage from './pages/InvoicesPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import authService from './services/authService';
import PersistentErrorNotification from './components/PersistentErrorNotification';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: Use a flag to prevent multiple auth checks
    let mounted = true;
    
    const checkAuth = async () => {
      console.log('[App] useEffect triggered - Checking auth on mount');
      console.log('[App] Current pathname:', window.location.pathname);
      
      // IMPORTANT: Don't do ANY auth checks on the login page
      if (window.location.pathname === '/login') {
        console.log('[App] On login page, skipping ALL auth checks');
        if (mounted) {
          setLoading(false);
        }
        return;
      }
      
      // Only check auth if we have both token and user
      if (authService.isAuthenticated()) {
        try {
          console.log('[App] Authenticated, getting current user...');
          const currentUser = await authService.getCurrentUser();
          if (mounted) {
            setUser(currentUser);
          }
        } catch (error) {
          console.error('[App] Failed to get current user:', error);
          // Don't set user to null - let it be handled by navigation
        }
      }
      
      if (mounted) {
        setLoading(false);
      }
    };

    checkAuth();
    
    return () => {
      mounted = false;
    };
  }, []);

  const handleLoginSuccess = (loggedInUser) => {
    console.log('[App] handleLoginSuccess called');
    setUser(loggedInUser);
  };

  const handleLogout = () => {
    console.log('[App] handleLogout called');
    setUser(null);
    authService.logout();
  };

  if (loading) {
    return (
      <div className="App loading">
        <div className="loading-spinner">Laden...</div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <PersistentErrorNotification />
        <Routes>
          <Route path="/login" element={
            <LoginForm key="login-page" onLoginSuccess={handleLoginSuccess} />
          } />
          <Route path="/" element={
            user ? (
              <Layout user={user} onLogout={handleLogout}>
                <Dashboard user={user} />
              </Layout>
            ) : <Navigate to="/login" />
          } />
          <Route path="/facilities" element={
            <ProtectedRoute user={user} requireAdmin={true}>
              <Layout user={user} onLogout={handleLogout}>
                <FacilitiesPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/bookings/import" element={
            <ProtectedRoute user={user} allowedRoles={['admin', 'backoffice']}>
              <Layout user={user} onLogout={handleLogout}>
                <BookingsImportPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/bookings" element={
            user ? (
              <Layout user={user} onLogout={handleLogout}>
                <BookingsPage />
              </Layout>
            ) : <Navigate to="/login" />
          } />
          <Route path="/orders" element={
            user ? (
              <Layout user={user} onLogout={handleLogout}>
                <OrdersPage />
              </Layout>
            ) : <Navigate to="/login" />
          } />
          <Route path="/users" element={
            <ProtectedRoute user={user} requireAdmin={true}>
              <Layout user={user} onLogout={handleLogout}>
                <UsersPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/invoices" element={
            <ProtectedRoute user={user} allowedRoles={['admin', 'backoffice']}>
              <Layout user={user} onLogout={handleLogout}>
                <InvoicesPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute user={user} allowedRoles={['admin', 'backoffice']}>
              <Layout user={user} onLogout={handleLogout}>
                <ReportsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute user={user} requireAdmin={true}>
              <Layout user={user} onLogout={handleLogout}>
                <SettingsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;